import React from 'react'
import {Badge, ListItem, ListItemIcon, ListItemText, useTheme} from '@mui/material'
import {alpha} from '@mui/material/styles'
import {Link, useLocation} from 'react-router-dom'
import {isPathActive} from './isPathActive'
import {NavItemConfig} from './types'

interface NavItemProps {
  item: NavItemConfig
  expanded: boolean
  onClick?: () => void
}

const NavItem = ({item, expanded, onClick}: NavItemProps): JSX.Element => {
  const location = useLocation()
  const theme = useTheme()

  const isActionItem = Boolean(item.onClick)
  const isActive = !isActionItem && isPathActive(location.pathname, item.path)

  const sharedSx = {
    px: expanded ? 2 : 0,
    py: 0,
    mb: 1,
    minHeight: 45,
    borderRadius: 1,
    justifyContent: expanded ? 'flex-start' : 'center',
    textDecoration: 'none',
    color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
    backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
    '&:hover': {
      backgroundColor: isActive
        ? alpha(theme.palette.primary.main, 0.14)
        : theme.palette.action.hover,
    },
  }

  const iconNode = (
    <ListItemIcon
      sx={{
        minWidth: expanded ? 40 : 'auto',
        justifyContent: 'center',
        color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
      }}
    >
      {(item.badge ?? 0) > 0 ? (
        <Badge badgeContent={item.badge} color="error">
          {item.icon}
        </Badge>
      ) : (
        item.icon
      )}
    </ListItemIcon>
  )

  const labelNode = expanded && (
    <ListItemText
      primary={item.label}
      primaryTypographyProps={{
        fontSize: 14,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? theme.palette.primary.main : 'text.primary',
      }}
    />
  )

  if (isActionItem) {
    return (
      <ListItem
        component="button"
        onClick={() => { item.onClick?.(); onClick?.() }}
        sx={{
          ...sharedSx,
          width: '100%',
          border: 'none',
          cursor: 'pointer',
          background: 'none',
        }}
      >
        {iconNode}
        {labelNode}
      </ListItem>
    )
  }

  return (
    <ListItem
      component={Link}
      to={item.path}
      onClick={onClick}
      sx={sharedSx}
    >
      {iconNode}
      {labelNode}
    </ListItem>
  )
}

export default NavItem
